import { captureReturnTo, clearReturnTo } from "./returnTo";
import { resetDesktopScreenTypes } from "./desktopScreenTypes";
import { syncedTokenSub } from "./syncedTokens";
import type { SessionExpiredInfo } from "./auth";

export interface SessionExpiryDeps {
  clearMfeTokens(): void;
  navigate(url: string): void;
  resetScreenTypes?(): Promise<unknown>;
}

export interface SessionRefusedDeps extends SessionExpiryDeps {
  clearSnapshot(cognitoSub: string): Promise<void>;
}

// Cognito refusing the refresh token is the account being shut down, and the
// snapshot is a claim that this identity was valid online. Left behind, it
// would let a removed user back in on the next outage. Cleared before the
// redirect, which would otherwise race the IPC call that clears it.
export async function handleSessionRefused(
  cognitoSub: string | null,
  deps: SessionRefusedDeps,
): Promise<void> {
  // Read before the teardown below wipes the bridged token it comes from.
  const sub = cognitoSub ?? syncedTokenSub();
  if (sub) await deps.clearSnapshot(sub);
  handleSessionExpired({ deliberate: false }, deps);
}

// A returnTo capture exists so an involuntary expiry (or a shared deep link
// opened while logged out) resolves back to the target page after re-login. A
// user-initiated sign-out is the opposite case: the page must not be left
// behind for whoever logs in next, so the capture is discarded rather than
// written. Conflating the two is what let a signed-out admin's page become the
// next user's landing page.
export function handleSessionExpired(
  { deliberate }: SessionExpiredInfo,
  deps: SessionExpiryDeps,
): void {
  deps.clearMfeTokens();
  if (deliberate) {
    clearReturnTo();
    // Not awaited: the navigation below must not wait on the IPC round-trip.
    // ipcRenderer.invoke posts the message before the renderer unloads, so the
    // main-process handler still runs even though the reply is discarded.
    void (deps.resetScreenTypes ?? resetDesktopScreenTypes)();
  }

  const { pathname } = window.location;
  if (
    pathname === "/login" ||
    pathname.startsWith("/login/") ||
    pathname.startsWith("/submerged")
  ) {
    return;
  }

  if (!deliberate) captureReturnTo();
  deps.navigate("/login");
}
